import Table from 'Components/Table';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';
import { useSelector } from 'react-redux';
import { isUndefined } from 'lodash';
import { getEmptyStringReplacementText } from 'Utils';
import {
    getUser,
    shouldShowManageTenants,
    shouldShowMerchants,
    shouldShowScheduledDeployment,
    shouldShowTenantGroups
} from 'Selectors';
import useStickyTableHead from 'Hooks/useStickyTableHead';

type ReactTableColumn = Column<DeploymentHistoryData>;

type FilteredColumns = ReactTableColumn[];
type AllColumns = Array<ReactTableColumn & { isHidden?: boolean }>;

type Props = {
    latestDeploymentHistory: DeploymentHistoryData | undefined;
    deploymentSummary: DeploymentSummary;
};

const TABLEID = 'revisions-current-deployment-table';

const CurrentDeploymentTable = ({ latestDeploymentHistory, deploymentSummary }: Props) => {
    const { t } = useTranslation();
    useStickyTableHead(TABLEID);
    const { landlordId, tenantKey } = useSelector(getUser);

    const windowParams = [
        'top=0',
        'left=0',
        `height=${window?.screen?.height}`,
        `width=${window?.screen?.width}`,
        'fullscreen=yes'
    ].join(',');
    const openWindow = (id: number) => {
        const windowUrl = `/revisions/${id}`;
        window?.open(windowUrl, windowUrl, windowParams);
    };

    const allColumns: AllColumns = [
        {
            Header: '',
            id: 'modify-scheduled-deployment',
            disableSortBy: true,
            width: 50,
            maxWidth: 50,
            renderType: 'overrideCell',
            Cell: () => {
                return null;
            },
            isHidden: !shouldShowScheduledDeployment()
        },
        {
            Header: 'Version ID',
            accessor: 'version',
            id: 'current-version',
            renderType: 'link',
            renderOptions: {
                to: 'revisions/$0',
                openNewWindow: true,
                newWindowParams: windowParams,
                iconName: 'expand',
                exceptions: [t('Sandbox')]
            },
            style: {
                width: '8%'
            },
            disableSortBy: true,
            dataType: 'number'
        },
        {
            Header: 'Comment',
            accessor: 'comment',
            id: 'current-comment',
            dataType: 'text',
            disableSortBy: true,
            style: {
                maxWidth: 'calc(20% - 40px)'
            }
        },
        {
            Header: 'Deployed',
            id: 'current-createdAt',
            dataType: 'datetime',
            accessor: 'createdAt',
            sortType: 'datetime',
            renderType: 'datetime',
            disableSortBy: true,
            style: {
                width: '10%'
            }
        },
        {
            Header: 'Deployed By',
            accessor: (d: any) => d?._formatted?.createdBy,
            id: 'current-createdBy',
            disableSortBy: true,
            dataType: 'text',
            style: {
                width: '10%'
            }
        },
        {
            Header: 'New Global Rules',
            accessor: 'newGlobalRules',
            id: 'current-newGlobalRules',
            dataType: 'number',
            disableSortBy: true,
            style: {
                width: '8%'
            }
        },
        {
            Header: 'Modified Global Rules',
            accessor: 'modifiedGlobalRules',
            id: 'current-modifiedGlobalRules',
            dataType: 'number',
            disableSortBy: true,
            style: {
                width: '8%'
            }
        },
        {
            Header: t('New Merchants'),
            accessor: 'newMerchants',
            id: 'current-newMerchants',
            disableSortBy: true,
            dataType: 'number',
            style: {
                width: '8%'
            },
            isHidden: !shouldShowMerchants()
        },
        {
            Header: t('Modified Merchants'),
            accessor: 'modifiedMerchants',
            id: 'current-modifiedMerchants',
            disableSortBy: true,
            dataType: 'number',
            style: {
                width: '8%'
            },
            isHidden: !shouldShowMerchants()
        },
        {
            Header: t('Deactivated Merchants'),
            accessor: 'deactivatedMerchants',
            id: 'current-deactivatedMerchants',
            disableSortBy: true,
            dataType: 'number',
            style: {
                width: '8%'
            },
            isHidden: !shouldShowMerchants()
        },
        {
            Header: t('New Tenant Groups'),
            accessor: 'newTenantGroups',
            id: 'newTenantGroups',
            style: {
                width: '7%'
            },
            dataType: 'number',
            isHidden: !shouldShowManageTenants(landlordId, tenantKey) && shouldShowTenantGroups()
        },
        {
            Header: t('Modified Tenant Groups'),
            accessor: 'modifiedTenantGroups',
            id: 'modifiedTenantGroups',
            style: {
                width: '7%'
            },
            dataType: 'number',
            isHidden: !shouldShowManageTenants(landlordId, tenantKey) && shouldShowTenantGroups()
        }
    ];

    const filteredColumns = allColumns.reduce((columns: FilteredColumns, column) => {
        if (!column.isHidden) {
            const { isHidden, ...resultColumn } = column;
            columns.push(resultColumn);
        }
        return columns;
    }, []);

    const data: DeploymentHistoryData[] = [
        {
            version: t('Sandbox'),
            comment: getEmptyStringReplacementText(undefined),
            createdAt: getEmptyStringReplacementText(undefined),
            createdBy: getEmptyStringReplacementText(undefined),
            newGlobalRules: deploymentSummary?.acquirerSummary?.addedRules,
            modifiedGlobalRules: deploymentSummary?.acquirerSummary?.modifiedRules,
            newMerchants: deploymentSummary?.merchantSummary?.added,
            modifiedMerchants:
                deploymentSummary?.merchantSummary?.modified + deploymentSummary?.merchantSummary?.disabled,
            deactivatedMerchants: deploymentSummary?.merchantSummary?.disabled
        }
    ];

    if (shouldShowManageTenants(landlordId, tenantKey) && shouldShowTenantGroups()) {
        data.forEach((revision) => {
            revision.modifiedTenantGroups =
                (revision?.modifiedTenantGroups ?? 0) + (revision?.removedTenantGroups ?? 0);
        });
    }

    if (!isUndefined(latestDeploymentHistory)) {
        data.push({ ...latestDeploymentHistory, newGlobalRules: latestDeploymentHistory?.addedRules ?? 0 });
        latestDeploymentHistory.modifiedMerchants =
            (latestDeploymentHistory?.modifiedMerchants || 0) + (latestDeploymentHistory?.deactivatedMerchants || 0);
    }

    return (
        <Table
            tableName='current-deployment-table'
            dataTestId={TABLEID}
            columns={filteredColumns}
            data={data || []}
            clickableRow
            onRowInteractCB={(row) => {
                if (row.id !== '0') openWindow(row?.values?.['current-version']);
            }}
            innerTableHeader={t('Current')}
        />
    );
};

export default React.memo(CurrentDeploymentTable);
